import PhoneIcon from '../../../../assets/icons/authenticators/imprivata_ID.svg?react';
import SuperviseIcon from '../../../../assets/icons/authenticators/supervise.svg?react';
import CameraIcon from '../../../../assets/icons/camera.svg?react';
import IdCardIcon from '../../../../assets/icons/identification.svg?react';
import MailIcon from '../../../../assets/icons/mail.svg?react';
import ContinueButton from '@components/continueButton/ContinueButton';
import { Form, Radio } from 'antd';
import type { FunctionComponent, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

/** Which assurance ceremony the clinician picked; each is served by its own API family. */
export enum AssurancePath {
  SUPERVISED = 'supervised',
  INDIVIDUAL = 'self',
}

enum FieldNames {
  PATH = 'path',
}

interface FormValues {
  [FieldNames.PATH]: AssurancePath;
}

const TRANSLATION = 'self-service.epcs-setup.start';

/** One prerequisite line: its copy key paired with the glyph the design puts in front of it. */
interface Need {
  key: string;
  Icon: FunctionComponent<SVGProps<SVGSVGElement>>;
}

// Both cards list the photo ID and the phone, so the copy lives under one
// `needs` block rather than being duplicated per path.
const NEED = {
  DESIGNATED_PERSON: { key: 'designated-person', Icon: SuperviseIcon },
  EMAIL: { key: 'email', Icon: MailIcon },
  PHONE: { key: 'phone', Icon: PhoneIcon },
  PHOTO_ID: { key: 'photo-id', Icon: IdCardIcon },
  FACE_CAPTURE: { key: 'face-capture', Icon: CameraIcon },
} satisfies Record<string, Need>;

const PATH_OPTIONS: readonly { path: AssurancePath; needs: readonly Need[] }[] = [
  { path: AssurancePath.SUPERVISED, needs: [NEED.DESIGNATED_PERSON, NEED.PHOTO_ID, NEED.PHONE] },
  {
    path: AssurancePath.INDIVIDUAL,
    needs: [NEED.EMAIL, NEED.PHONE, NEED.PHOTO_ID, NEED.FACE_CAPTURE],
  },
];

interface EpcsStep1StartProps {
  /** Names the phone in the prerequisite lines, as the Imprivata ID step names it in its title. */
  deviceName: string;
  /** True while the assurance this screen started is still being created. */
  busy: boolean;
  onContinue: (path: AssurancePath) => void;
  cancelFlow: () => void;
}

const EpcsStep1Start = ({ deviceName, busy, onContinue, cancelFlow }: EpcsStep1StartProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();

  return (
    <Form<FormValues>
      className="continue-layout epcs-start"
      form={form}
      initialValues={{ [FieldNames.PATH]: AssurancePath.SUPERVISED }}
      onFinish={values => onContinue(values[FieldNames.PATH])}
    >
      <div>
        <h1 className="title">{t(`${TRANSLATION}.title`)}</h1>
        <p>{t(`${TRANSLATION}.choose`)}</p>

        <Form.Item name={FieldNames.PATH}>
          <Radio.Group className="epcs-path-options">
            {PATH_OPTIONS.map(({ path, needs }) => (
              <Radio key={path} value={path} className="epcs-path-option">
                <span className="epcs-path-option-label">{t(`${TRANSLATION}.${path}.label`)}</span>
                <span className="epcs-path-option-needs-title">{t(`${TRANSLATION}.you-need`)}</span>
                <span className="epcs-path-option-needs">
                  {needs.map(({ key, Icon }) => (
                    <span key={key} className="epcs-path-option-need">
                      <Icon aria-hidden="true" focusable="false" />
                      <span>{t(`${TRANSLATION}.needs.${key}`, { deviceName })}</span>
                    </span>
                  ))}
                </span>
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </div>

      <div>
        <ContinueButton
          htmlType="submit"
          loading={busy}
          secondaryButton={{ label: t('common.do-later'), onClick: cancelFlow }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </div>
    </Form>
  );
};

export default EpcsStep1Start;
